import { callAgroApi } from '@services/agroapi';
import { GeoJSON } from './GeoJSON';
import { getRoot, Instance, types } from 'mobx-state-tree';
import { Crop } from './Crop';
import { Label } from './LabelStore';
import { Season } from './SeasonStore';
import i18n from '@services/next-i18n';
import { LFDateFormat, computeSeedAmountPerUnitArea, dateSortValidation, formatIncomeUnits, isPresent, luxonUserLocale } from '@farmai-shared/LFUtils';
import { DateTime } from 'luxon';
import * as turf from '@turf/turf';
import { withDateProcessors } from './DateTimeUtility';
import { Traceability } from './Traceability';
import { basicSort, calculateDateDiff, selectCropQualityUnit } from '@services/utils';
import { ActivityResponse, CropzoneResponse, SoilSampleResponse, TransactionResponse, VIResponse, ViSmoothResponse } from '@services/responseTypes';
import { Transformers } from '@services/transformer';
import { IRootStoreAny } from './rootUtilType';
import { IVI, IVISnapshotIn, VI } from './VI';
import { SupportedMapLayers } from './enums';
import { ITransaction, ITransactionSnapshotIn } from './TransactionStore';
import { uiStore } from '@stores/UiStore';
import { CropzoneFormData } from '@app/_components/common/CropzoneInputs/utils';

// additional explanation https://gitlab.com/listenfield/products/farmai/lf-dashboard/-/merge_requests/1126#note_1994621071
const MULTIBAND_ENABLED_AT = DateTime.fromISO('2024-01-18T00:00:00.000Z');
const BUFFER_DAYS = 5;
const fetchAndTransformVI = (id: string, path: string, params: string, transformer: (vi: VIResponse) => IVISnapshotIn | null) => callAgroApi<VIResponse[]>(`/cropzones/${id}/${path}?${params}`, 'GET').then(({
  data
}) => {
  if (data.length > 0) {
    return data.reduce((acc: IVI[], vi) => {
      const transformed = transformer(vi);
      if (transformed !== null) {
        acc.push(VI.create(transformed));
      }
      return acc;
    }, []);
  }
  return [];
});
const fetchSmoothedVI = (id: string, path: string, source: string, viType?: string) => callAgroApi<ViSmoothResponse[]>(`/cropzones/${id}/${path}/smoothed?source=${source}${path === 's2multi' && isPresent(viType) ? `&band=${viType}` : ''}`, 'GET').then(({
  data
}) => data);
const sortElements = (elements, lang: string, timezone: string, order?: 'asc') => elements.sort((a, b) => {
  const {
    first,
    second
  } = dateSortValidation(LFDateFormat.datetimeFromISO(a.datetime, timezone, lang), LFDateFormat.datetimeFromISO(b.datetime, timezone, lang));
  return basicSort(first, second, order);
});
export const Cropzone = withDateProcessors(GeoJSON.named('Cropzone').props({
  children: types.array(types.late(() => Cropzone)),
  crop: types.safeReference(types.late(() => Crop), {
    get(identifier, parent) {
      const cropStore = getRoot<IRootStoreAny>(parent).cropStore;
      return cropStore?.crops.find(f => f.id === identifier);
    },
    set(value) {
      return value.name;
    }
  }),
  traceability: types.maybe(types.late(() => Traceability)),
  labels: types.array(types.safeReference(types.late(() => Label), {
    get(identifier, parent) {
      const uiStore = getRoot<IRootStoreAny>(parent).uiStore;
      return uiStore?.currentOrganization.labelStore.labels.get(identifier);
    },
    set(value) {
      return value.id;
    }
  })),
  season: types.maybeNull(types.safeReference(types.late(() => Season), {
    get(identifier, parent) {
      const uiStore = getRoot<IRootStoreAny>(parent).uiStore;
      return uiStore?.currentOrganization.seasonStore.seasons.get(identifier);
    },
    set(value) {
      return value.id;
    }
  }))
}).views(self => ({
  get root() {
    return getRoot<IRootStoreAny>(self);
  }
})).views(self => ({
  get slug() {
    return self.properties.get('slug');
  },
  get startDate(): DateTime {
    return self.properties.get('startDate');
  },
  get startDateZ(): DateTime {
    const lang = self.root.userStore.user.settings.lang;
    return self.properties.get('startDate').setZone(self.timezone).reconfigure({
      outputCalendar: lang === 'th' ? 'Buddhist' : 'gregory'
    }).setLocale(luxonUserLocale(lang));
  },
  get endDatetime(): DateTime | null {
    return self.properties.get('endDate');
  },
  get endDatetimeZ(): DateTime | null {
    const lang = self.root.userStore.user.settings.lang;
    return isPresent(self.properties.get('endDate')) ? self.properties.get('endDate').setZone(self.timezone).reconfigure({
      outputCalendar: lang === 'th' ? 'Buddhist' : 'gregory'
    }).setLocale(luxonUserLocale(lang)) : null;
  },
  get rsStoppedAt(): DateTime | null {
    return self.properties.get('rsStoppedAt');
  },
  get plantingDate(): DateTime | null {
    return self.properties.get('plantingDate');
  },
  get plantingDateZ(): DateTime | null {
    const lang = self.root.userStore.user.settings.lang;
    return isPresent(self.properties.get('plantingDate')) && lang ? self.properties.get('plantingDate').setZone(self.timezone).reconfigure({
      outputCalendar: lang === 'th' ? 'Buddhist' : 'gregory'
    }).setLocale(luxonUserLocale(lang)) : null;
  },
  get generation() {
    return self.properties.get('generation');
  },
  get prevCropzoneId() {
    return self.properties.get('prevCropzone')?.get('id');
  },
  get nextCropzoneId() {
    return self.properties.get('nextCropzone')?.get('id');
  },
  get archivedAt(): DateTime | null {
    return self.properties.get('archivedAt');
  },
  get archivedAtZ(): DateTime | null {
    const lang = self.root.userStore.user.settings.lang;
    return isPresent(self.properties.get('archivedAt')) && lang ? self.properties.get('archivedAt').setZone(self.timezone).reconfigure({
      outputCalendar: lang === 'th' ? 'Buddhist' : 'gregory'
    }).setLocale(luxonUserLocale(lang)) : null;
  },
  get field() {
    return self.properties.get('field').toJSON();
  },
  get country() {
    return self.properties.get('country')?.toJSON() ?? {};
  },
  get region() {
    return self.properties.get('region')?.toJSON() ?? {};
  },
  get district() {
    return self.properties.get('district')?.toJSON() ?? {};
  },
  get subdistrict() {
    return self.properties.get('subdistrict')?.toJSON() ?? {};
  },
  get predicted() {
    return self.properties.get('predicted').toJSON();
  },
  get amountOfSeeds() {
    return self.properties.get('amountOfSeeds');
  },
  get amountOfSeeds_unit() {
    return self.properties.get('amountOfSeeds_unit');
  },
  get amountOfSeedsComputed() {
    const areaUnit = self.root.userStore.user.settings.area_unit;
    return self.properties && self.properties.get('amountOfSeeds') ? computeSeedAmountPerUnitArea(Number(self.properties && self.properties.get('amountOfSeeds')) || 0, self.area || 0, String(areaUnit)) || 0 : 0;
  },
  get actual() {
    return self.properties.get('actual').toJSON();
  },
  get activitiesCount() {
    return self.properties.get('activitiesCount');
  },
  get soilSamplesCount() {
    return self.properties.get('soilSamplesCount');
  },
  get distance() {
    return self.properties.get('distance');
  },
  get dap() {
    return self.properties.get('dap');
  },
  get farm() {
    return self.properties.get('farm').toJSON();
  },
  get supportGenerations() {
    return self.crop?.name === 'sugarcane';
  },
  get latestSoilSampleAt() {
    return self.properties.get('latestSoilSampleAt');
  },
  get latestSoilSampleAtZ(): DateTime | null {
    const lang = self.root.userStore.user.settings.lang;
    return isPresent(self.properties.get('latestSoilSampleAt')) && lang ? self.properties.get('latestSoilSampleAt').setZone(self.timezone).reconfigure({
      outputCalendar: lang === 'th' ? 'Buddhist' : 'gregory'
    }).setLocale(luxonUserLocale(lang)) : null;
  }
})).views(self => ({
  get isArchived() {
    return self.archivedAt !== null;
  },
  get predictedHarvest() {
    return isPresent(self.predicted?.harvest) ? parseFloat(self.predicted.harvest) : null;
  },
  get actualHarvest() {
    return isPresent(self.actual?.harvest) ? parseFloat(self.actual.harvest) : null;
  },
  get predictedYield() {
    return isPresent(self.predicted?.yield) ? parseFloat(self.predicted.yield) : null;
  },
  get predictedYieldAtDAP() {
    return isPresent(self.properties.get('predicted')?.get('yieldComputedAt')) ? self.properties.get('predicted').get('yieldComputedAt').diff(self.properties.get('plantingDate'), ['days']).toFormat('d') : null;
  },
  get actualYield() {
    return isPresent(self.actual?.yield) ? parseFloat(self.actual.yield) : null;
  },
  get predictedCCS() {
    return isPresent(self.predicted?.ccs) ? parseFloat(self.predicted.ccs) : null;
  },
  get predictedCCSAtDAP() {
    return calculateDateDiff(self.properties.get('plantingDate'), self.properties.get('predicted')?.get('ccsComputedAt'));
  },
  get distanceFromUser() {
    return self.distance && self.distance > 1000 ? `${(self.distance / 1000).toFixed(1)} ${i18n.t('km')}` : `${self.distance} ${i18n.t('m')}`;
  },
  get maturityDateZ(): DateTime | null {
    const lang = self.root.userStore.user.settings.lang;
    return isPresent(self.properties.get('predicted')?.get('maturityDate')) ? self.properties.get('predicted').get('maturityDate').setZone(self.timezone).reconfigure({
      outputCalendar: lang === 'th' ? 'buddhist' : 'gregory'
    }).setLocale(luxonUserLocale(lang)) : null;
  },
  get maturityDateComputedAtDAP() {
    return calculateDateDiff(self.properties.get('plantingDate'), self.properties.get('predicted')?.get('maturityDateComputedAt'));
  },
  get harvestingDateZ(): DateTime | null {
    const lang = self.root.userStore.user.settings.lang;
    return isPresent(self.properties.get('actual')?.get('harvestingDate')) ? self.properties.get('actual').get('harvestingDate').setZone(self.timezone).reconfigure({
      outputCalendar: lang === 'th' ? 'buddhist' : 'gregory'
    }).setLocale(luxonUserLocale(lang)) : null;
  },
  get harvestComputedAtDAP() {
    return calculateDateDiff(self.properties.get('plantingDate'), self.properties.get('predicted')?.get('harvestComputedAt'));
  },
  get transactions() {
    const transactionsArray: ITransaction[] = Array.from(self.root.uiStore.currentOrganization?.txStore.transactions.values() || []);
    return transactionsArray.filter((tx: ITransaction) => tx.cropzone_id === self.id);
  }
})).actions(self => ({
  handleTxes(txes: any[]): ITransaction[] {
    if (!self.root?.uiStore?.currentOrganization) {
      return [];
    }
    const transactions: ITransactionSnapshotIn[] = [];
    for (const tx of txes) {
      if (tx.source?.type === 'Place') {
        self.root.uiStore.currentOrganization.placeStore.pushToPlaces(tx.source);
      }
      if (tx.destination) {
        self.root.uiStore.currentOrganization.placeStore.pushToPlaces(tx.destination);
      }
      transactions.push({
        id: tx.id,
        destination: tx.destination?.id,
        source: tx.source?.id || null,
        cropzone_id: tx.cropzone_id,
        datetime: tx.datetime,
        timezone: tx.timezone,
        qtyExchanged: tx.qtyExchanged,
        extra: tx.extra
      });
    }
    return self.root.uiStore.currentOrganization.txStore.replaceTxes(transactions);
  }
})).views(self => ({
  formatQueryDates(year: number) {
    return {
      from: encodeURIComponent(DateTime.fromObject({
        year: year
      }).startOf('year').toISO()),
      to: encodeURIComponent(DateTime.fromObject({
        year: year
      }).endOf('year').toISO())
    };
  }
})).actions(self => ({
  archive() {
    return callAgroApi<{
      message: 'success';
    }>(`/cropzones/${self.id}/archive`, 'POST');
  },
  delete() {
    return callAgroApi<{
      message: 'success';
    }>(`/cropzones/${self.id}`, 'DELETE');
  },
  update(data: CropzoneFormData & {
    pending?: boolean;
  }) {
    const lang = self.root.userStore.user.settings.lang;
    const amountOfSeeds = self.properties.get('amountOfSeeds');
    return callAgroApi<CropzoneResponse>(`/organizations/${data.orgId}/cropzones/${self.id}`, 'PATCH', {
      crop_id: data.crop.value,
      generation: data.generation?.value,
      planting_date: data.plantingDate ? data.plantingDate : null,
      ...(isPresent(amountOfSeeds) || isPresent(data.amountOfSeeds) ? {
        seed_usage: isPresent(data.amountOfSeeds) ? Number(data.amountOfSeeds) : 0,
        seed_usage_unit: data.amountOfSeeds_unit?.value || uiStore.currentOrganization?.seed_usage_unit
      } : {}),
      labels: data.labels?.map(l => ({
        id: l.value
      })),
      ...((isPresent(data.production) || isPresent(data.harvestDate) || isPresent(self.actualHarvest) || isPresent(self.harvestingDateZ)) && {
        actual: {
          harvest: isPresent(data.production) ? Number(data.production) : null,
          harvesting_date: isPresent(data.harvestDate) ? data.harvestDate : null
        }
      }),
      ...(isPresent(data.location) && {
        location: data.location
      }),
      pending: data.pending || false
    });
  },
  updateCropProgress(orgId: string, crop_progress: string) {
    return callAgroApi<CropzoneResponse>(`/organizations/${orgId}/cropzones/${self.id}`, 'PATCH', {
      crop_progress
    });
  },
  updateDistance(position: number[]) {
    const coordinates = self.getPoint?.coordinates;
    if (!isPresent(coordinates)) return;
    self.setProperty('distance', turf.distance(turf.point(position), turf.point(coordinates), {
      units: 'kilometers'
    }) * 1000);
  },
  fetchFertilizerActivities(page = 1) {
    const fertilizerTypeId = self.root.activityTypeStore.activityTypes.find(a => a.canonical_name === 'fertilization')?.id;
    return callAgroApi<ActivityResponse[]>(`/cropzones/${self.id}/activities?filter[activity_type_id]=${fertilizerTypeId}&page=${page.toString()}`, 'GET').then(({
      data
    }) => data.map(d => ({
      ...Transformers.activity(d),
      activityType: d.activity_type.id,
      cropzone: d.cropzone?.id
    }))).then(activities => self.root.uiStore.currentOrganization.pushToActivities(activities));
  },
  fetchSoilObservationActivities(page = 1) {
    const soilObsTypeId = self.root.activityTypeStore.activityTypes.find(a => a.canonical_name === 'soil_observation')?.id;
    const lang = self.root.userStore.user.settings.lang;
    return callAgroApi<ActivityResponse[]>(`/cropzones/${self.id}/activities?filter[activity_type_id]=${soilObsTypeId}&page=${page.toString()}`, 'GET').then(({
      data
    }) => self.root.uiStore.currentOrganization.pushToActivities(data.map(d => ({
      ...Transformers.activity(d),
      activityType: d.activity_type.id,
      cropzone: d.cropzone?.id
    })))).then(activities => sortElements(activities, lang, self.timezone));
  },
  fetchSoilSamples() {
    const lang = self.root.userStore.user.settings.lang;
    return callAgroApi<SoilSampleResponse[]>(`/cropzones/${self.id}/soil_samples`, 'GET').then(response => response.data.map(d => Transformers.soilSample(d))).then(samples => sortElements(samples, lang, self.timezone, 'asc'));
  },
  getVi(viType: SupportedMapLayers) {
    const dateQuery = self.startDate ? `from=${self.startDate.minus({
      days: BUFFER_DAYS
    })}&to=${self.rsStoppedAt || DateTime.now().endOf('day').toISO()}` : '';
    const viSource = self.root.uiStore.currentOrganization.hasPlanetSupport && [SupportedMapLayers.NDVI, SupportedMapLayers.EVI].includes(viType) ? 'PLANET' : 'sentinel_2A';

    // planet not currently possible to return multiband
    const path = viSource === 'PLANET' ? viType : 's2multi';
    const params = [dateQuery, `source=${viSource}`].join('&');
    const multibandViTransformer = (vi: VIResponse) => Transformers.multibandVi(vi, viType);
    return fetchAndTransformVI(self.id, path, params, path === 's2multi' ? multibandViTransformer : Transformers.vi).then(vis => {
      // first attempt results
      if (isPresent(vis)) {
        return vis;
      }
      if (viSource === 'PLANET') {
        if (self.createdAt > MULTIBAND_ENABLED_AT) {
          // second attempt get multiband
          return fetchAndTransformVI(self.id, 's2multi', params.replace('PLANET', 'sentinel_2A'),
          // source must be sentinel_2A for s2multi
          multibandViTransformer);
        }
        // second attempt get original
        return fetchAndTransformVI(self.id, viType, params, Transformers.vi);
      }

      // second attempt get original
      return fetchAndTransformVI(self.id, viType, params, Transformers.vi);
    });
  },
  fetchSmoothedVI(viType: SupportedMapLayers) {
    const viSource = self.root.uiStore.currentOrganization.hasPlanetSupport && [SupportedMapLayers.NDVI, SupportedMapLayers.EVI].includes(viType) ? 'PLANET' : 'sentinel_2A';
    if (viSource === 'PLANET') {
      return fetchSmoothedVI(self.id, viType, viSource).then(data => {
        if (isPresent(data)) {
          return data;
        }
        // Fallback to primary source
        return fetchSmoothedVI(self.id, 's2multi', 'sentinel_2A', viType);
      });
    }

    // Default case for sentinel_2A as primary source
    return fetchSmoothedVI(self.id, 's2multi', 'sentinel_2A', viType).then(data => {
      if (!isPresent(data)) {
        // Fallback if primary data is not present
        return fetchSmoothedVI(self.id, viType, viSource);
      }
      return data;
    });
  },
  createTransaction(params: {
    source_id: string | null;
    destination_id: string;
    datetime: string;
    qty_exchanged: number;
    extra: {
      income?: number;
      income_unit?: string;
      crop_quality?: number;
      crop_quality_unit?: string;
    };
  }) {
    return callAgroApi<TransactionResponse>(`/cropzones/${self.id}/txes`, 'POST', params).then(({
      data
    }) => {
      const tx = Transformers.tx(data, self.timezone);
      return tx;
    });
  },
  fetchTransactions({
    page = 1,
    season,
    year
  }: {
    page?: number;
    season?: string;
    year?: number;
  }) {
    let queryString = '';
    if (season) {
      queryString = [season ? `filter[season_id]=${season}` : null].filter(Boolean).join('&');
    } else {
      const dates = self.formatQueryDates(year ?? DateTime.now().year);
      queryString = [dates.from ? `filter[from]=${dates.from}` : null, dates.to ? `filter[to]=${dates.to}` : null].filter(Boolean).join('&');
    }
    return callAgroApi<TransactionResponse[]>(`/cropzones/${self.id}/txes?page=${page.toString()}&${queryString}`, 'GET').then(({
      data
    }) => {
      const transformed = data.map(d => Transformers.tx(d, self.timezone));
      self.handleTxes(transformed);
      return transformed;
    });
  }
})).views(self => ({
  get isPastRsStoppedAt() {
    return isPresent(self.rsStoppedAt) ? self.rsStoppedAt < DateTime.now() : false;
  },
  get isPastHarvest() {
    return isPresent(self.properties.get('actual')?.harvestingDate) ? self.properties.get('actual')?.get('harvestingDate') < DateTime.now() : false;
  },
  get status() {
    if (self.isArchived) return i18n.t('archived');
    return i18n.t('current');
  },
  get generationModalTitle() {
    if (!isPresent(self.generation) && !isPresent(self.plantingDate)) {
      return 'GenerationAndPlantingMustBeFilledIn';
    }
    if (!isPresent(self.generation)) {
      return 'GenerationMustBeFilledIn';
    }
    return 'PlantingMustBeFilledIn';
  },
  get sourceOptions() {
    const options = self.transactions.reduce((acc, tx) => {
      const destination = tx.destination;
      if (destination && isPresent(destination.id) && isPresent(destination.option)) {
        acc.set(destination.id, destination.option);
      }
      return acc;
    }, new Map<string, {
      label: string;
      value: string | null;
    }>());
    return [{
      label: self.farm.name!,
      value: self.farm.id
    }, ...options.values()];
  },
  get cropQuality() {
    const quality: {
      unit: string;
      value: number;
    }[] = [];
    if (!self.traceability?.crop_quality) {
      return quality;
    }
    for (const [unit, value] of self.traceability.crop_quality) {
      quality.push({
        value: value,
        unit: unit
      });
    }
    return quality;
  },
  get lastTransactionDestination() {
    return self.traceability?.harvest_destination_places[self.traceability?.harvest_destination_places.length - 1];
  },
  get cropQualityValue() {
    if (!self.crop) return null;
    const cropUnit = selectCropQualityUnit(self.crop.rawName.toLowerCase());
    return cropUnit && self.traceability?.crop_quality.get(cropUnit);
  },
  get cropQualityUnit(): string | null {
    if (!self.crop) return null;
    return selectCropQualityUnit(self.crop.rawName);
  },
  get formatIncome() {
    if (self.traceability?.income_unit === 'satang') {
      return self.traceability?.income ? self.traceability?.income / 100 : null;
    }
    return self.traceability?.income;
  },
  get displayIncome() {
    if (self.traceability?.income_unit === 'satang') {
      return self.traceability?.income ? self.traceability?.income / 100 : null;
    }
    return self.traceability?.income;
  },
  get incomeUnit() {
    if (!self.traceability?.income_unit) return i18n.t('baht');
    return i18n.t(formatIncomeUnits(self.traceability?.income_unit) || 'baht');
  }
})));
export type ICropzone = Instance<typeof Cropzone>;